import { db } from '@/firebase/init'
import { LabEquipment } from '@/globals/typescript/models/LabEquipment'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import type {
  ILabEquipmentMaintenance,
  ILabEquipment,
} from '@team-milva/lab-models/labEquipment'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { orderBy } from 'lodash-es'
import { defineStore } from 'pinia'

type TUpdateLabEquipmentValues = {
  type?: string
  name?: string
  serialNumber?: string
  nextMaintenanceDate?: string | null
  analysisIDs?: string[]
  activeState?: ILabEquipment['activeState']
}

export const useLabEquipmentStore = defineStore('labEquipment', {
  state: () => ({
    labEquipment: [] as LabEquipment[],
    labEquipmentMaintenance: [] as ILabEquipmentMaintenance[],

    unsubscribeEquipment: null as (() => void) | null,
    unsubscribeLabEquipmentMaintenance: null as (() => void) | null,

    maintenanceFilterType: '' as string,
    maintenanceFilterName: '' as string,
    maintenanceDateRange: [null, null] as [Date | null, Date | null],
  }),

  actions: {
    getlabEquipment(): Promise<LabEquipment[]> {
      if (this.labEquipmentLoaded) {
        return Promise.resolve(this.labEquipment)
      }

      const accountStore = useAccountStore()
      if (!accountStore.labAccount?.id) {
        return Promise.resolve([])
      }

      return new Promise((resolve) => {
        const { currentLocale } = useAppStore()

        let resolved = false

        const unsubscribe = onSnapshot(
          query(
            collection(db, 'Apps', '-lab-management-', 'LabEquipment'),
            where('labAccountID', '==', accountStore.labAccount!.id)
          ),
          (snapshot) => {
            this.labEquipment = orderBy(
              snapshot.docs.map(
                (doc) => new LabEquipment(doc.data() as ILabEquipment)
              ),
              [(e) => e.getName(currentLocale).toLowerCase()],
              ['asc']
            )
            this.unsubscribeEquipment = unsubscribe

            if (!resolved) {
              resolved = true
              resolve(this.labEquipment)
            }
          }
        )
      })
    },

    async createLabEquipment({
      labAccountID,
      data,
    }: {
      labAccountID: string
      data: {
        type: string
        name: string
        serialNumber: string
        analysisIDs: string[]
      }
    }): Promise<void> {
      const { currentLocale } = useAppStore()
      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/lab-equipment',
        {
          path: { labAccountID },
          body: {
            type: { [currentLocale]: data.type },
            name: { [currentLocale]: data.name },
            serialNumber: data.serialNumber,
            analysisIDs: data.analysisIDs,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateLabEquipment(
      labEquipmentID: string,
      values: TUpdateLabEquipmentValues
    ): Promise<void> {
      const accountStore = useAccountStore()
      const { currentLocale } = useAppStore()

      const body: {
        type?: Record<string, string>
        name?: Record<string, string>
        serialNumber?: string
        nextMaintenanceDate?: string | null
        analysisIDs?: string[]
        activeState?: ILabEquipment['activeState']
      } = {}

      if (values.type !== undefined) {
        body['type'] = { [currentLocale]: values.type }
      }
      if (values.name !== undefined) {
        body['name'] = { [currentLocale]: values.name }
      }
      if (values.serialNumber !== undefined) {
        body['serialNumber'] = values.serialNumber
      }
      if (values.nextMaintenanceDate !== undefined) {
        body['nextMaintenanceDate'] = values.nextMaintenanceDate
      }
      if (values.analysisIDs !== undefined) {
        body['analysisIDs'] = values.analysisIDs
      }
      if (values.activeState !== undefined) {
        body['activeState'] = values.activeState
      }

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/lab-equipment/{labEquipmentID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            labEquipmentID,
          },
          body,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deactivateLabEquipment(labEquipmentID: string): Promise<void> {
      await this.updateLabEquipment(labEquipmentID, {
        activeState: 'inactive',
      })
    },

    async reactivateLabEquipment(labEquipmentID: string): Promise<void> {
      await this.updateLabEquipment(labEquipmentID, {
        activeState: 'active',
      })
    },

    async addLabEquipmentMaintenance({
      labEquipmentID,
      date,
      nextMaintenanceDate,
    }: {
      labEquipmentID: string
      date: string
      nextMaintenanceDate?: string | null
    }): Promise<void> {
      const accountStore = useAccountStore()

      if (nextMaintenanceDate) {
        await this.updateLabEquipment(labEquipmentID, {
          nextMaintenanceDate,
        })
      }

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/lab-equipment/{labEquipmentID}/maintenance',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            labEquipmentID,
          },
          body: {
            date,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateLabEquipmentMaintenance({
      labEquipmentID,
      labEquipmentMaintenanceID,
      date,
    }: {
      labEquipmentID: string
      labEquipmentMaintenanceID: string
      date: string
    }): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/lab-equipment/{labEquipmentID}/maintenance/{labEquipmentMaintenanceID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            labEquipmentID,
            labEquipmentMaintenanceID,
          },
          body: {
            date,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deleteLabEquipmentMaintenance({
      labEquipmentID,
      labEquipmentMaintenanceID,
    }: {
      labEquipmentID: string
      labEquipmentMaintenanceID: string
    }): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'delete',
        'lab-accounts/{labAccountID}/lab-equipment/{labEquipmentID}/maintenance/{labEquipmentMaintenanceID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            labEquipmentID,
            labEquipmentMaintenanceID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    getLabEquipmentMaintenance(): Promise<ILabEquipmentMaintenance[]> {
      if (this.labEquipmentMaintenanceLoaded) {
        return Promise.resolve(this.labEquipmentMaintenance)
      }

      const accountStore = useAccountStore()
      if (!accountStore.labAccount?.id) {
        return Promise.resolve([])
      }

      return new Promise((resolve) => {
        let resolved = false

        const unsubscribe = onSnapshot(
          query(
            collection(
              db,
              'Apps',
              '-lab-management-',
              'LabEquipmentMaintenance'
            ),
            where('labAccountID', '==', accountStore.labAccount!.id)
          ),
          (snapshot) => {
            this.labEquipmentMaintenance = snapshot.docs
              .map((doc) => doc.data() as ILabEquipmentMaintenance)
              .filter((entry) => entry.activeState !== 'deleted')
            this.unsubscribeLabEquipmentMaintenance = unsubscribe

            if (!resolved) {
              resolved = true
              resolve(this.labEquipmentMaintenance)
            }
          }
        )
      })
    },

    resetStore(): void {
      this.labEquipment = []
      if (this.unsubscribeEquipment) {
        this.unsubscribeEquipment()
        this.unsubscribeEquipment = null
      }

      this.labEquipmentMaintenance = []
      if (this.unsubscribeLabEquipmentMaintenance) {
        this.unsubscribeLabEquipmentMaintenance()
        this.unsubscribeLabEquipmentMaintenance = null
      }

      this.maintenanceFilterType = ''
      this.maintenanceFilterName = ''
      this.maintenanceDateRange = [null, null]
    },
  },

  getters: {
    labEquipmentLoaded(state): boolean {
      return state.unsubscribeEquipment !== null
    },
    labEquipmentMaintenanceLoaded(state): boolean {
      return state.unsubscribeLabEquipmentMaintenance !== null
    },

    labEquipmentTypes(): string[] {
      const { currentLocale } = useAppStore()
      return [
        ...new Set(
          this.labEquipment.map((e) => e.getType(currentLocale)).filter(Boolean)
        ),
      ]
    },
  },
})

